const { errorMetadata, sanitizedError, sanitizeExceptionProperties } = require('./PostHogErrors');
const ALLOWED_HOSTS = new Set(['https://us.i.posthog.com']);
const PUBLIC_TOKEN = /^phc_[A-Za-z0-9_-]{20,}$/;
const SAFE_IDENTIFIER = /^[A-Za-z0-9_.:-]{1,128}$/;
const COMPLETION_ACTIONS = Object.freeze({
    purchase_order_created: 'vendor',
    approval_requested: 'vendor',
    invoice_submitted: 'vendor',
    invitation_requested: 'persona',
});
const noCompletion = () => false;

function safeString(value) {
    if (Number.isSafeInteger(value) && value >= 0) return String(value);
    return typeof value === 'string' && SAFE_IDENTIFIER.test(value) ? value : undefined;
}

function decodeClaims(token) {
    try {
        const payload = token.split('.')[1];
        if (!payload) return undefined;
        const encoded = payload.replace(/-/g, '+').replace(/_/g, '/');
        const json = typeof atob === 'function'
            ? atob(encoded)
            : Buffer.from(encoded, 'base64').toString('utf8');
        return JSON.parse(json);
    } catch (_) {
        return undefined;
    }
}

function getConfiguration(configuration) {
    const enabled = configuration && (configuration.posthogEnabled === true || configuration.posthogEnabled === 'true');
    const token = configuration && configuration.posthogProjectToken;
    const host = configuration && configuration.posthogHost;
    const environment = configuration && configuration.posthogEnvironment;
    const resolvedEnvironment = typeof environment === 'string' ? environment.trim() : '';

    if (!enabled || !PUBLIC_TOKEN.test(token || '') || !ALLOWED_HOSTS.has(host) || !SAFE_IDENTIFIER.test(resolvedEnvironment) || /[#{]/.test(resolvedEnvironment)) {
        return undefined;
    }

    return { token, host, environment: resolvedEnvironment };
}

function isPersonaRoute(pathname) {
    return typeof pathname === 'string' && /^\/persona(?:\/|$)/i.test(pathname);
}

function getAccountContext({ token, personaToken, pathname, isValidToken }) {
    const personaRoute = isPersonaRoute(pathname);
    const personaClaims = isValidToken(personaToken) ? decodeClaims(personaToken) : undefined;
    const selectedVendorClaims = isValidToken(token) ? decodeClaims(token) : undefined;
    if (selectedVendorClaims && selectedVendorClaims.sub === 'ARIUSER') return undefined;
    if (personaRoute && !personaClaims) return undefined;

    const persona = !!personaClaims || (selectedVendorClaims && selectedVendorClaims.aud === 'ARIEXTERNALSIGNON');
    const claims = personaClaims || selectedVendorClaims;
    if (!claims) return undefined;

    // Persona navigation renders before the store-sign-out effect clears its token.
    // Use the destination context immediately so that Redux cleanup cannot emit a second pageview.
    const selectedVendor = !personaRoute && selectedVendorClaims && selectedVendorClaims.aud === 'ARIEXTERNALSIGNON'
        ? selectedVendorClaims.vendor
        : undefined;
    const vendor = persona ? selectedVendor : claims;
    const vendorId = safeString(vendor && (vendor.id || vendor.corporateId));
    const personaId = persona && safeString(claims.id);
    // Do not associate a store token left over from another persona session.
    if (personaClaims && selectedVendorClaims && selectedVendorClaims.id && safeString(selectedVendorClaims.id) !== personaId) return undefined;
    if ((!persona && !vendorId) || (persona && !personaId)) return undefined;

    const audience = Array.isArray(claims.aud) ? claims.aud[0] : claims.aud;
    const accountKind = persona ? 'persona_account' : 'vendor_account';
    return {
        accountId: persona ? `partner-connect:persona:${personaId}` : `partner-connect:account:${vendorId}`,
        vendorId,
        accountKind,
        audience: safeString(audience),
    };
}

function routeTemplate(pathname) {
    const routes = [
        [/^\/$/i, 'home'],
        [/^\/carWash$/i, 'car_wash'],
        [/^\/carWash\/vehicle\/[^/]+$/i, 'car_wash_vehicle'],
        [/^\/po\/search$/i, 'purchase_order_search'],
        [/^\/po\/odometer\/[^/]+$/i, 'purchase_order_odometer'],
        [/^\/po\/repairHistory\/[^/]+$/i, 'purchase_order_repair_history'],
        [/^\/po\/driverValidationComplaints\/[^/]+$/i, 'purchase_order_driver_validation_complaints'],
        [/^\/po\/complaint\/[^/]+$/i, 'purchase_order_complaint'],
        [/^\/po\/close\/[^/]+$/i, 'purchase_order_close'],
        [/^\/po\/clientParameters\/[^/]+$/i, 'purchase_order_client_parameters'],
        [/^\/po\/actionRequired\/[^/]+$/i, 'purchase_order_action_required'],
        [/^\/po\/adjustment\/[^/]+$/i, 'purchase_order_adjustment'],
        [/^\/po\/contact\/[^/]+$/i, 'purchase_order_contact'],
        [/^\/po\/view\/[^/]+$/i, 'purchase_order_view'],
        [/^\/po\/[^/]+\/[^/]+$/i, 'purchase_order_edit'],
        [/^\/vehicle\/[^/]+\/documents$/i, 'vehicle_documents'],
        [/^\/vehicles\/(vin8|plate|clientId|serviceNo)\/.+$/i, 'vehicle_search_results'],
        [/^\/yourAccount(\/myUnits)?$/i, 'your_account'],
        [/^\/linkedVendorAccount(\/Register)?$/i, 'linked_vendor_account'],
        [/^\/dealerDrafts(\/[^/]+\/[^/]+)?$/i, 'dealer_drafts'],
        [/^\/attentionNeeded$/i, 'attention_needed'],
        [/^\/contact\/(HelpFAQ|FeedbackForm)$/i, 'contact'],
        [/^\/Stores$/i, 'stores'],
        [/^\/StoreProfile\/[^/]+$/i, 'store_profile'],
        [/^\/Persona\/AccessStores$/i, 'persona_access_stores'],
        [/^\/Persona\/Stores$/i, 'persona_stores'],
        [/^\/Persona\/Stores\/[^/]+\/users\/add$/i, 'persona_add_store_users'],
        [/^\/Persona\/Stores\/[^/]+$/i, 'persona_store_profile'],
        [/^\/Persona\/Users$/i, 'persona_users'],
        [/^\/Persona\/Users\/add$/i, 'persona_add_users'],
        [/^\/Persona\/Users\/[^/]+\/stores\/add$/i, 'persona_add_user_stores'],
        [/^\/Persona\/Users\/[^/]+\/Stores\/[^/]+\/edit$/i, 'persona_user_permissions'],
        [/^\/Persona\/Users\/[^/]+$/i, 'persona_user_profile'],
        [/^\/Persona\/.+$/i, 'persona'],
        [/^\/AccidentManagement\/.+$/i, 'accident_management'],
        [/^\/reviewAccountInformation$/i, 'review_account_information'],
        [/^\/learnMore\/[^/]+$/i, 'learn_more'],
        [/^\/tlsWarning\/learnMore$/i, 'browser_security_information'],
        [/^\/paymentStatusBulletin\/learnMore$/i, 'payment_status_information'],
        [/^\/vendorEnrollment\/request(?:\/detail\/[^/]+)?$/i, 'vendor_enrollment'],
    ];
    // Match React Router's default case-insensitive paths and optional trailing slash.
    const path = typeof pathname === 'string' ? pathname.replace(/\/+$/, '') || '/' : '';
    const matched = routes.find(([pattern]) => pattern.test(path));
    return matched ? matched[1] : 'other_authenticated_route';
}

function allowedProperties(properties) {
    const allowed = ['route_template', 'environment', 'account_id', 'account_kind', 'vendor_id', 'audience'];
    return allowed.reduce((result, key) => {
        if (typeof properties[key] === 'string' && SAFE_IDENTIFIER.test(properties[key])) result[key] = properties[key];
        if (key === 'vendor_id' && properties[key] === null) result[key] = null;
        return result;
    }, {});
}

function allowedTransportProperties(properties) {
    const allowed = ['token', 'distinct_id', '$anon_distinct_id', '$device_id', '$user_id', '$is_identified', '$session_id', '$window_id', '$lib', '$lib_version', '$process_person_profile'];
    const result = allowed.reduce((safe, key) => {
        if (typeof properties[key] === 'string' || typeof properties[key] === 'boolean') safe[key] = properties[key];
        return safe;
    }, {});
    const setProperties = allowedProperties(properties.$set || {});
    const setOnceProperties = allowedProperties(properties.$set_once || {});
    if (Object.keys(setProperties).length) result.$set = setProperties;
    if (Object.keys(setOnceProperties).length) result.$set_once = setOnceProperties;
    return { ...result, ...allowedProperties(properties) };
}

function sanitizeEvent(event) {
    const permittedEvents = new Set(['$pageview', '$identify', '$set', '$exception', 'partner_connect_account_context', 'partner_connect_store_selected', 'partner_connect_action_completed']);
    if (!event || !permittedEvents.has(event.event)) return null;
    const sanitized = { event: event.event, properties: { ...allowedTransportProperties(event.properties || {}), $geoip_disable: true } };
    if (event.event === '$exception') {
        const exception = sanitizeExceptionProperties(event.properties || {});
        if (!exception) return null;
        Object.assign(sanitized.properties, exception);
    }
    if (event.event === 'partner_connect_action_completed') {
        const action = event.properties && event.properties.action;
        if (typeof action !== 'string' || !Object.prototype.hasOwnProperty.call(COMPLETION_ACTIONS, action)) return null;
        sanitized.properties.action = action;
    }
    if (typeof event.timestamp === 'string' || typeof event.timestamp === 'number') sanitized.timestamp = event.timestamp;
    if (event.timestamp instanceof Date && Number.isFinite(event.timestamp.getTime())) sanitized.timestamp = event.timestamp.toISOString();
    if (typeof event.uuid === 'string' && SAFE_IDENTIFIER.test(event.uuid)) sanitized.uuid = event.uuid;
    const setProperties = allowedProperties(event.$set || {});
    const setOnceProperties = allowedProperties(event.$set_once || {});
    if (Object.keys(setProperties).length) sanitized.$set = setProperties;
    if (Object.keys(setOnceProperties).length) sanitized.$set_once = setOnceProperties;
    return sanitized;
}

let sdkLoad;
function loadPostHogSdk() {
    if (sdkLoad) return sdkLoad;
    const asset = typeof document === 'undefined' ? null : document.querySelector('meta[name="partner-connect-posthog"]');
    const source = asset && asset.getAttribute('content');
    const integrity = asset && asset.getAttribute('data-integrity');
    if (!source || !source.startsWith('/vendor/posthog/') || !/^sha384-[A-Za-z0-9+/]{64}$/.test(integrity || '')) {
        return Promise.reject(new Error('PostHog asset metadata is missing or invalid.'));
    }
    sdkLoad = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = source;
        script.integrity = integrity;
        script.crossOrigin = 'anonymous';
        script.async = true;
        const failed = () => {
            sdkLoad = undefined;
            script.remove();
            reject(new Error('PostHog SDK could not load.'));
        };
        script.onload = () => window.partnerConnectPostHog ? resolve() : failed();
        script.onerror = failed;
        document.head.appendChild(script);
    });
    return sdkLoad;
}

function createPostHogRuntime({ getSdk = () => typeof window === 'undefined' ? undefined : window.partnerConnectPostHog, loadSdk = loadPostHogSdk, getEventTarget = () => typeof window === 'undefined' ? undefined : window, now = Date.now } = {}) {
    let initialized = false;
    let loading = false;
    let latestOptions;
    let resetRequired = false;
    let lastAccountId;
    let lastPageKey;
    let lastContextKey;
    let lastSelectedVendor;
    let completionGeneration = 0;
    let eventTarget;
    let reporting = false;
    let reportedErrors = new WeakSet();
    // API translation strips request authorization. Keep those object identities suppressed
    // across account resets, even when the original capture was disabled, stale, or failed.
    // Weak references avoid mutating UI errors or retaining their payloads/credentials.
    const translatedApiErrors = new WeakSet();
    let errorWindow = 0;
    let errorCount = 0;

    const onError = event => { if (event.error) captureError(event.error, 'browser'); };
    const onRejection = event => captureError(event.reason, 'unhandled_rejection');

    function stopErrorListeners() {
        if (!eventTarget) return;
        eventTarget.removeEventListener('error', onError);
        eventTarget.removeEventListener('unhandledrejection', onRejection);
        eventTarget = undefined;
    }

    function startErrorListeners() {
        if (eventTarget) return;
        const target = getEventTarget();
        if (!target || typeof target.addEventListener !== 'function') return;
        target.addEventListener('error', onError);
        target.addEventListener('unhandledrejection', onRejection);
        eventTarget = target;
    }

    function captureError(error, source = 'browser', translatedError) {
        if (source === 'api' && translatedError && typeof translatedError === 'object') translatedApiErrors.add(translatedError);
        if (source !== 'api' && error && typeof error === 'object' && translatedApiErrors.has(error)) return false;
        // Never let optional telemetry affect application error handling or trigger itself recursively.
        if (reporting || !initialized || resetRequired || !lastAccountId || !latestOptions) return false;
        reporting = true;
        try {
            const settings = getConfiguration(latestOptions.configuration);
            const context = getAccountContext(latestOptions);
            const sdk = getSdk();
            if (!settings || !context || context.accountId !== lastAccountId || !sdk || typeof sdk.captureException !== 'function') return false;
            // An old request may reject after a store/account change. Only attribute API errors whose
            // authorization header still matches this session; never send that header to analytics.
            const authorization = error && error.config && error.config.headers && error.config.headers.Authorization;
            if ((source === 'api' || authorization) && (!authorization || ![latestOptions.token, latestOptions.personaToken].includes(authorization))) return false;
            if (error && typeof error === 'object' && reportedErrors.has(error)) return false;
            const time = now();
            if (time - errorWindow >= 60000) { errorWindow = time; errorCount = 0; }
            if (errorCount >= 20) return false;
            const metadata = errorMetadata(source, error && error.response && error.response.status);
            const safe = sanitizedError(error, metadata.error_source, metadata.http_status);
            errorCount++;
            sdk.captureException(safe, {
                // Global/render failures identify the observing session, not the origin of old
                // async work. Only request-bound failures can safely claim vendor attribution.
                ...allowedProperties({ account_id: context.accountId, account_kind: context.accountKind, vendor_id: source === 'api' && authorization === latestOptions.token ? context.vendorId || null : null, audience: context.audience, environment: settings.environment, route_template: routeTemplate(latestOptions.pathname) }),
                ...metadata,
            });
            if (error && typeof error === 'object') reportedErrors.add(error);
            return true;
        } catch (_) {
            return false;
        } finally {
            reporting = false;
        }
    }

    // Bind a confirmed API outcome to its starting account/store and safe route.
    // No queue, SDK loading, or business-state changes; optional telemetry always fails closed.
    function prepareCompletion(action, authorization) {
        try {
            if (typeof action !== 'string' || !Object.prototype.hasOwnProperty.call(COMPLETION_ACTIONS, action) || !initialized || resetRequired || !latestOptions) return noCompletion;
            const settings = getConfiguration(latestOptions.configuration);
            const context = getAccountContext(latestOptions);
            const scope = COMPLETION_ACTIONS[action];
            const currentAuthorization = scope === 'persona' ? latestOptions.personaToken : latestOptions.token;
            if (!settings || !context || context.accountId !== lastAccountId || !authorization || authorization !== currentAuthorization) return noCompletion;
            if (scope === 'vendor' && !context.vendorId) return noCompletion;
            const generation = completionGeneration;
            const properties = allowedProperties({
                account_id: context.accountId, account_kind: context.accountKind,
                vendor_id: scope === 'vendor' ? context.vendorId : null,
                audience: context.audience, environment: settings.environment,
                route_template: routeTemplate(latestOptions.pathname),
            });
            let completed = false;
            return () => {
                if (completed) return false;
                completed = true;
                try {
                    if (!initialized || resetRequired || completionGeneration !== generation || !latestOptions) return false;
                    const currentSettings = getConfiguration(latestOptions.configuration);
                    const currentContext = getAccountContext(latestOptions);
                    const currentToken = scope === 'persona' ? latestOptions.personaToken : latestOptions.token;
                    const sdk = getSdk();
                    if (!currentSettings || currentSettings.token !== settings.token || currentSettings.environment !== settings.environment ||
                        !currentContext || currentContext.accountId !== context.accountId || currentContext.vendorId !== context.vendorId ||
                        authorization !== currentToken || !sdk || typeof sdk.capture !== 'function') return false;
                    sdk.capture('partner_connect_action_completed', { ...properties, action });
                    return true;
                } catch (_) { return false; }
            };
        } catch (_) { return noCompletion; }
    }

    function reset() {
        completionGeneration++;
        stopErrorListeners();
        resetRequired = true;
        const sdk = getSdk();
        if (!sdk || typeof sdk.reset !== 'function') return false;
        try { sdk.reset(); } catch (_) { return false; }
        resetRequired = false;
        lastAccountId = undefined;
        lastPageKey = undefined;
        lastContextKey = undefined;
        lastSelectedVendor = undefined;
        reportedErrors = new WeakSet();
        return true;
    }

    function sync(options) {
        latestOptions = options;
        const { configuration, token, personaToken, pathname, isValidToken } = options;
        const settings = getConfiguration(configuration);
        const context = getAccountContext({ token, personaToken, pathname, isValidToken });
        if (!settings || !context) {
            stopErrorListeners();
            if (lastAccountId) reset();
            return false;
        }

        const sdk = getSdk();
        try {
            if (!sdk || typeof sdk.init !== 'function') {
                if (!loading) {
                    loading = true;
                    Promise.resolve().then(loadSdk).then(() => {
                        loading = false;
                        const loadedSdk = getSdk();
                        if (loadedSdk && typeof loadedSdk.init === 'function') sync(latestOptions);
                    }).catch(() => { loading = false; });
                }
                return false;
            }
            if (!initialized) {
                sdk.init(settings.token, {
                    api_host: settings.host,
                    autocapture: false,
                    capture_pageview: false,
                    capture_pageleave: false,
                    capture_performance: false,
                    capture_exceptions: false,
                    capture_dead_clicks: false,
                    capture_heatmaps: false,
                    disable_session_recording: true,
                    disable_surveys: true,
                    advanced_disable_flags: true,
                    advanced_disable_feature_flags: true,
                    disable_external_dependency_loading: true,
                    save_referrer: false,
                    save_campaign_params: false,
                    error_tracking: { autocapture: false },
                    persistence: 'memory',
                    // No anonymous activity is captured, so do not merge a fresh anonymous ID on every reload.
                    reuseAnonymousId: true,
                    person_profiles: 'identified_only',
                    before_send: sanitizeEvent,
                });
                initialized = true;
            }

            // Never attribute a new session while the SDK may still retain the previous identity.
            if ((resetRequired || (lastAccountId && lastAccountId !== context.accountId)) && !reset()) return false;
            lastAccountId = context.accountId;
            const properties = allowedProperties({
                account_id: context.accountId,
                account_kind: context.accountKind,
                vendor_id: context.vendorId || null,
                audience: context.audience,
                environment: settings.environment,
            });
            const contextKey = JSON.stringify(properties);
            if (lastContextKey !== contextKey) {
                completionGeneration++;
                if (typeof sdk.identify === 'function') sdk.identify(context.accountId, properties);
                if (typeof sdk.capture === 'function') sdk.capture('partner_connect_account_context', properties);
                lastContextKey = contextKey;
            }

            const pageProperties = allowedProperties({ ...properties, route_template: routeTemplate(pathname) });
            // A store token on AccessStores is an in-flight selection. Count only the subsequent
            // authenticated vendor route, before its pageview so store funnels retain their order.
            if (context.accountKind === 'persona_account' && context.vendorId && lastSelectedVendor !== context.vendorId && typeof sdk.capture === 'function') {
                sdk.capture('partner_connect_store_selected', pageProperties);
            }
            lastSelectedVendor = context.vendorId;
            const pageKey = `${contextKey}|${pathname || ''}`;
            if (lastPageKey !== pageKey && typeof sdk.capture === 'function') {
                sdk.capture('$pageview', pageProperties);
                lastPageKey = pageKey;
            }
            startErrorListeners();
            return true;
        } catch (_) {
            stopErrorListeners();
            initialized = false;
            return false;
        }
    }

    return { sync, reset, captureError, prepareCompletion };
}

const postHogRuntime = createPostHogRuntime();

module.exports = {
    createPostHogRuntime,
    getAccountContext,
    getConfiguration,
    isPersonaRoute,
    routeTemplate,
    sanitizeEvent,
    preparePostHogCompletion: (action, authorization) => postHogRuntime.prepareCompletion(action, authorization),
    capturePostHogError: (error, source, translatedError) => postHogRuntime.captureError(error, source, translatedError),
    syncPostHogAnalytics: (options) => postHogRuntime.sync(options),
};
